import { useLocalStorage } from '@vueuse/core';
import { buildStreamingStatusScript } from '~/lib/streaming-status';

type StartBroadcastResponse = {
  id: null | string;
  key: string;
  rtmp: string;
  chat_url?: string;

  broadcast_id: null | string;
  channel_name: null | string;
  platform_id: null | string;
  region: null | string;
  chat_id: null | string;
};

export const useGoLiveStore = defineStore('go-live', () => {
  const api = useApiStore();
  const tracking = useTrackingStore();
  const dialogs = useDialogsStore();
  const obs = useObsStore();

  const currentBroadcast = ref<{
    startResponse: StartBroadcastResponse;
    platformType: MultistreamChatSupportedPlatform;
  }>();

  const stopPreviousBroadcast = async () => {
    if (currentBroadcast.value) {
      stopBroadcast(
        currentBroadcast.value.platformType,
        currentBroadcast.value.startResponse?.broadcast_id || 'all',
      );

      currentBroadcast.value = undefined;
    }
  };

  const startBroadcast = async (
    platformType: MultistreamChatSupportedPlatform,
    payload: Record<string, any>,
  ) => {
    stopPreviousBroadcast();

    const response = await api.post<StartBroadcastResponse>(
      `/api/v5/obs-plugin/go-live/${platformType}/stream/start`,
      payload,
    );

    currentBroadcast.value = {
      startResponse: response.data,
      platformType,
    };

    return response;
  };

  const stopBroadcast = async (
    platformType: MultistreamChatSupportedPlatform,
    broadcastId: string,
  ) => {
    await api.post(
      `/api/v5/obs-plugin/go-live/${platformType}/stream/${broadcastId}/end`,
    );
  };

  tracking.on('stopped_streaming', async () => {
    stopPreviousBroadcast();
    setAndBroadcastStreamingStatus(false);
  });

  const applyForTikTokStreaming = async () => {
    dialogs.showDialog('TIKTOK_APPLY');
  };

  const title = useLocalStorage('golive/shared/title', '');
  const description = useLocalStorage('golive/shared/description', '');

  const updateTitle = (val: string) => {
    title.value = val;
  };
  const updateDescription = (val: string) => {
    description.value = val;
  };

  // Docks cannot seem to access OBS globals, so we maintain our own state
  // and update through window event handlers, we could probably explore
  // persisted local storage, but anything else was a limitation from the
  // backend side (stores in docks) and should be revisited there.
  const isStreaming = ref(false);
  const setStreamingStatus = (isLive: boolean) => {
    isStreaming.value = isLive;
  };

  const broadcastStreamingStatus = (
    isLive: boolean,
    destinations: DestinationPlatformWithLiveInfo[],
  ) => {
    const chatDock = obs.getDockByTitle('Streamlabs: Chat');
    chatDock?.executeJavascript(
      buildStreamingStatusScript(isLive, destinations),
    );
  };

  const setAndBroadcastStreamingStatus = (
    isLive: boolean,
    destinations: DestinationPlatformWithLiveInfo[] = [],
  ) => {
    setStreamingStatus(isLive);
    broadcastStreamingStatus(isLive, destinations);
  };

  const destinations = ref<DestinationPlatformWithLiveInfo[]>([]);
  const setDestinations = (value: DestinationPlatformWithLiveInfo[]) => {
    destinations.value = value;
  };

  return {
    startBroadcast,
    stopBroadcast,

    applyForTikTokStreaming,

    title,
    description,
    updateTitle,
    updateDescription,

    isStreaming,
    setStreamingStatus,
    setAndBroadcastStreamingStatus,
    destinations,
    setDestinations,
  };
});

// Stores are not kept in sync in docks
declare global {
  interface Window {
    consumeStreamingStatus: (
      isLive: boolean,
      destinations: DestinationPlatformWithLiveInfo[],
    ) => void;
  }
}

// TODO: would `window.postMessage` be worth it to avoid polluting globals?
export const onStreamingStatusChange = (
  cb: (
    isLive: boolean,
    destinations: DestinationPlatformWithLiveInfo[],
  ) => void,
) => {
  window.consumeStreamingStatus = async (isLive, destinations) => {
    await cb(isLive, destinations);
  };
};
